const axes = [
  {
    label: 'Ⅰ 戦略整合性',
    desc: '事業方針と評価指標の連動度合いを診断。経営目標が現場の行動に落ちているかを検証します。',
  },
  {
    label: 'Ⅱ 制度設計妥当性',
    desc: '等級・報酬体系の理論的整合を評価。学術的フレームワークに基づき制度の妥当性を判定します。',
  },
  {
    label: 'Ⅲ 運用実行力',
    desc: '現場管理職の制度活用レベルを測定。評価面談の質・頻度・フィードバック精度を確認します。',
  },
  {
    label: 'Ⅳ 社員納得度',
    desc: 'フィードバック機能と制度への信頼度を確認。評価の透明性と社員の受容度を数値化します。',
  },
  {
    label: 'Ⅴ 改善成熟度',
    desc: '環境変化への自律的修正力を評価。制度が時代・事業変化に対応できているかを診断します。',
  },
];

const scores = [0.40, 0.30, 0.55, 0.28, 0.60];

function RadarChart() {
  const cx = 170;
  const cy = 175;
  const r = 120;
  const numAxes = 5;
  const levels = [0.25, 0.5, 0.75, 1.0];

  const getPoint = (index: number, scale: number) => {
    const angle = -Math.PI / 2 + (index * 2 * Math.PI) / numAxes;
    return {
      x: cx + r * scale * Math.cos(angle),
      y: cy + r * scale * Math.sin(angle),
    };
  };

  const gridPolygons = levels.map((level) =>
    Array.from({ length: numAxes }, (_, i) => {
      const p = getPoint(i, level);
      return `${p.x},${p.y}`;
    }).join(' ')
  );

  const dataPolygon = scores
    .map((score, i) => {
      const p = getPoint(i, score);
      return `${p.x},${p.y}`;
    })
    .join(' ');

  const axisEndpoints = Array.from({ length: numAxes }, (_, i) => getPoint(i, 1.0));
  const dataDots = scores.map((score, i) => getPoint(i, score));

  const labelOffsets = [
    { dx: 0, dy: -18 },
    { dx: 16, dy: 0 },
    { dx: 10, dy: 18 },
    { dx: -10, dy: 18 },
    { dx: -16, dy: 0 },
  ];

  const labelPositions = Array.from({ length: numAxes }, (_, i) => {
    const p = getPoint(i, 1.22);
    return { x: p.x + labelOffsets[i].dx, y: p.y + labelOffsets[i].dy };
  });

  const labelTexts = ['Ⅰ 戦略整合性', 'Ⅱ 制度設計妥当性', 'Ⅲ 運用実行力', 'Ⅳ 社員納得度', 'Ⅴ 改善成熟度'];

  return (
    <svg width="340" height="360" viewBox="0 0 340 360" aria-label="人事制度診断レーダーチャート（サンプル）">
      {/* Grid polygons */}
      {gridPolygons.map((points, i) => (
        <polygon
          key={i}
          points={points}
          fill="none"
          stroke="#D0DCF0"
          strokeWidth={i === 3 ? 1.5 : 1}
        />
      ))}

      {/* Axis lines */}
      {axisEndpoints.map((ep, i) => (
        <line
          key={i}
          x1={cx}
          y1={cy}
          x2={ep.x}
          y2={ep.y}
          stroke="#D0DCF0"
          strokeWidth="1"
        />
      ))}

      {/* Data polygon */}
      <polygon
        points={dataPolygon}
        fill="rgba(26,92,176,0.18)"
        stroke="#1A5CB0"
        strokeWidth="2"
      />

      {/* Data dots */}
      {dataDots.map((dot, i) => (
        <circle key={i} cx={dot.x} cy={dot.y} r={5} fill="#2D8EFF" />
      ))}

      {/* Labels */}
      {labelPositions.map((pos, i) => (
        <text
          key={i}
          x={pos.x}
          y={pos.y}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="11"
          fontFamily="'Noto Sans JP', sans-serif"
          fontWeight="500"
          fill="#0D2B55"
        >
          {labelTexts[i]}
        </text>
      ))}

      {/* Center dot */}
      <circle cx={cx} cy={cy} r={3} fill="#D0DCF0" />
    </svg>
  );
}

export default function DiagnosisFramework() {
  return (
    <section
      className="py-16 bg-white"
      style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
    >
      <div className="max-w-[1080px] mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-2xl lg:text-[34px] font-bold text-[#1A1A2E] leading-tight mb-3">
            学術理論×実務知見で診断する5つの評価領域
          </h2>
          <p className="text-[#667085] text-sm">
            Miles &amp; Snow理論・Lepak &amp; Snell理論をベースに診断設計
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left: Radar Chart */}
          <div className="flex flex-col items-center">
            <div className="bg-[#F7F9FC] rounded-xl p-6 w-full flex flex-col items-center" style={{ boxShadow: '0 4px 24px rgba(13,43,85,0.08)' }}>
              <RadarChart />
              <p className="text-[#667085] text-sm text-center mt-2">
                ※ サンプルスコア。診断後に貴社の実数値をご確認いただけます
              </p>
            </div>
          </div>

          {/* Right: Description List */}
          <div className="flex flex-col gap-4">
            {axes.map((axis, index) => (
              <div
                key={index}
                className="border-l-4 border-[#1A5CB0] pl-5 py-3 rounded-r-lg bg-[#F7F9FC] hover:bg-[#EEF4FB] transition-colors cursor-default group"
              >
                <h3 className="text-[#0D2B55] text-base font-bold mb-1 group-hover:text-[#1A5CB0] transition-colors">
                  {axis.label}
                </h3>
                <p className="text-[#667085] text-sm leading-relaxed">{axis.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
